/**
 * 課金（M4）。サーバーは apps/server/src/modules/payments（developerRoutes.ts・README.md）。
 *
 *   - 売上: 通貨ごとの合計（売上・返金 + チャージバック・手数料の見積もり・手取り）と 30 日の日次（UTC）
 *   - 受け取りの状態と「お金」の画面への入口（登録・出金はクリエイター単位なので「お金」に 1 つだけ。2026-09-27 task/dashboard）
 *   - 直近の購入（50 件）: 状態・購入者（Players と同じドロワー）・全額返金 → PaymentsPurchases.tsx
 *   - いまどの決済で動いているか（偽決済 / Stripe テスト / 本番）を最初に見せる
 *
 * WHY 決済モードを一番上に出すか: 「テストのつもりで本物のカードを切った / 本番なのに偽決済のまま」が
 * 課金で一番高くつく取り違え。環境の帯（ProjectShell）とは別に、決済そのもののモードを明示する。
 *
 * 金額は API から最小通貨単位（usd = セント）で届く。表示は money.ts（Intl の桁数に従う）。
 * 通貨はまたいで合算・換算しない（為替レートを持たない。合計を 1 つの数字にすると嘘になる）。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { envPath, type EnvName, type PaymentMode, type PaymentsView } from '../api';
import { LineChart } from '../components/LineChart';
import { Empty, ErrorState, PageHeader, Spinner, StatTile } from '../components/ui';
import { useResource } from '../hooks';
import { useT } from '../i18n';
import type { PayeeView } from '../moneyApi';
import { payeeHeadline } from '../moneyLogic';
import { fromMajor, money, percentFromBps, toMajor } from '../money';
import { modeLabel, PurchasesCard } from './PaymentsPurchases';
import { useShell } from './ProjectShell';
import { projectEyebrow } from '../projectNav';

export function PaymentsPage() {
  const { project, env } = useShell();
  const base = envPath(project.id, env);
  const r = useResource<PaymentsView>(`${base}/payments`);
  const [currency, setCurrency] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { t, tr } = useT();
  const envName = t(`env.inline.${env}`);
  // 環境を切り替えたら前の環境の通知・通貨選択を持ち越さない
  useEffect(() => {
    setNotice(null);
    setCurrency(null);
  }, [base]);

  const data = r.data;
  // 既定の通貨 = 売上（gross）が一番大きい通貨
  const totals = useMemo(() => [...(data?.totals ?? [])].sort((a, b) => b.gross - a.gross), [data]);
  const cur = totals.find((t) => t.currency === currency) ?? totals[0] ?? null;

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('pay.title')}>
        {data && <span className={`pill ${data.mode === 'live' ? 'pill-accent' : ''}`}>{data.mode ? modeLabel(data.mode) : t('pay.notConfigured')}</span>}
      </PageHeader>
      {r.loading && !data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('pay.title')} onRetry={r.reload} />}
      {notice && (
        <div className="alert alert-ok" role="status">
          {notice}
        </div>
      )}
      {data && (
        <>
          <ModeNotice mode={data.mode} env={env} />

          {!cur ? (
            <section className="card">
              <Empty title={t('pay.noSalesTitle', { env: envName })}>
                <p>
                  {tr('pay.noSalesBody', {
                    products: <code>products</code>,
                    link: <Link to="../config">{t('pay.configLink')}</Link>,
                    call: <code>kumo.store.purchase('gem_pack')</code>
                  })}
                </p>
              </Empty>
            </section>
          ) : (
            <>
              {totals.length > 1 && (
                <div className="lb-meta">
                  <span className="muted small">{t('pay.perCurrency')}</span>
                  <div className="seg" role="group" aria-label={t('pay.currency')}>
                    {totals.map((t) => (
                      <button
                        key={t.currency}
                        type="button"
                        className={`seg-btn ${cur.currency === t.currency ? 'is-active' : ''}`}
                        aria-pressed={cur.currency === t.currency}
                        onClick={() => setCurrency(t.currency)}
                      >
                        {t.currency.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <section className="tiles" aria-label={t('pay.revenueIn', { currency: cur.currency.toUpperCase() })}>
                <StatTile
                  label={t('pay.gross')}
                  value={money(cur.gross, cur.currency)}
                  hint={t('pay.paidPurchases', { count: cur.purchases })}
                  tone="accent"
                />
                <StatTile
                  label={t('pay.refunds')}
                  value={money(cur.refunds + cur.disputes, cur.currency)}
                  hint={cur.disputes ? t('pay.inclDisputed', { amount: money(cur.disputes, cur.currency) }) : t('pay.refundsHint')}
                  tone={cur.refunds + cur.disputes > 0 ? 'warn' : undefined}
                />
                <StatTile label={t('pay.fee')} value={money(cur.feeEstimate, cur.currency)} hint={t('pay.feeHint', { pct: percentFromBps(data.feeBps) })} />
                <StatTile label={t('pay.net')} value={money(cur.developerEstimate, cur.currency)} hint={t('pay.netHint')} />
              </section>

              <section className="card">
                <div className="card-head">
                  <h2 className="card-title">{t('pay.daily')}</h2>
                  <span className="muted small">{t('pay.dailyRange', { currency: cur.currency.toUpperCase() })}</span>
                </div>
                {/* チャートは主単位（4.99）で描く（目盛りの「きりの良い数」が通貨の単位で決まるように）。書式で最小単位に戻して money() に通す */}
                <LineChart
                  data={data.daily.map((d) => ({ date: d.date, value: toMajor(d.gross[cur.currency] ?? 0, cur.currency) }))}
                  label={t('pay.dailyLabel', { currency: cur.currency.toUpperCase() })}
                  format={(v) => money(fromMajor(v, cur.currency), cur.currency)}
                  tickFormat={(v) => money(fromMajor(v, cur.currency), cur.currency, { compact: true })}
                />
              </section>
            </>
          )}

          <PayoutsCard env={env} mode={data.mode} />

          <PurchasesCard
            base={base}
            env={env}
            purchases={data.recent}
            onRefunded={(msg) => {
              setNotice(msg);
              r.reload();
            }}
          />
        </>
      )}
    </>
  );
}

function ModeNotice({ mode, env }: { mode: PaymentMode | null; env: EnvName }) {
  const { t, tr } = useT();
  const keyVar = env === 'production' ? 'STRIPE_LIVE_SECRET_KEY' : 'STRIPE_TEST_SECRET_KEY';
  if (mode === null) {
    return (
      <div className="alert alert-warn" role="note">
        <strong>{t('pay.notice.noneTitle')}</strong>
        <span>{tr('pay.notice.noneBody', { code: <code>payments_not_configured</code>, keyVar: <code>{keyVar}</code> })}</span>
      </div>
    );
  }
  if (mode === 'fake') {
    return (
      <div className={`alert ${env === 'production' ? 'alert-warn' : 'alert-info'}`} role="note">
        <strong>{t('pay.notice.fakeTitle')}</strong>
        <span>{tr('pay.notice.fakeBody', { env: t(`env.inline.${env}`), keyVar: <code>{keyVar}</code> })}</span>
      </div>
    );
  }
  if (mode === 'test') {
    return (
      <div className="alert alert-info" role="note">
        <strong>{t('pay.notice.testTitle')}</strong>
        <span>{tr('pay.notice.testBody', { card: <code>4242 4242 4242 4242</code> })}</span>
      </div>
    );
  }
  return null;
}

/**
 * 受け取り（旧: Stripe Connect Express の登録ボタン）。2026-09-27 task/dashboard で置き換え:
 * 受取人はゲームごとではなくクリエイターごと（modules/money の payees）なので、登録・出金は「お金」の画面に 1 つだけ置き、
 * ここには状態と入口だけを出す。production の環境 = 本番（live）の受取人、development = テスト（test）の受取人。
 * 旧 API（/v1/projects/:id/payments/connect）は決済にも送金にも使われていない（payments/README）ので画面からは呼ばない。
 */
function PayoutsCard({ env, mode }: { env: EnvName; mode: PaymentMode | null }) {
  const { t, tr } = useT();
  const livemode = env === 'production';
  const moneyMode = livemode ? 'live' : 'test';
  const r = useResource<PayeeView>(`/v1/money/payee?livemode=${livemode}`);
  const headline = r.data ? payeeHeadline(r.data.payee, r.data.eligibility) : null;
  const ready = headline === 'ready';
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{t('pay.payoutsNew')}</h2>
        {headline && <span className={`pill ${ready ? 'pill-ok' : headline === 'not_started' ? '' : 'pill-warn'}`}>{t(`payee.headline.${headline}`)}</span>}
      </div>
      <p className="small">{t('pay.payoutsNewLead')}</p>
      {r.error && <ErrorState error={r.error} what={t('pay.payoutsNew')} onRetry={r.reload} />}
      {mode === 'live' && headline && !ready && (
        <div className="alert alert-warn" role="note">
          <strong>{t('pay.liveBlockedTitle')}</strong>
          <span>{tr('pay.liveBlockedBody', { code: <code>payouts_not_configured</code> })}</span>
        </div>
      )}
      <div className="form-actions">
        <Link to={`/money/${moneyMode}/earnings`} className="btn btn-ghost">
          {t('pay.openMoney')}
        </Link>
        {headline && !ready && (
          <Link to={`/money/${moneyMode}/get-paid`} className="btn btn-primary">
            {headline === 'not_started' ? t('payee.start') : t('payee.continue')}
          </Link>
        )}
      </div>
    </section>
  );
}
